import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ConfigEntityRow } from '../api';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

export default function Drafts({ session }: Props) {
  const [drafts, setDrafts] = useState<ConfigEntityRow[]>([]);
  const [lint, setLint] = useState<{ problems: unknown[]; has_fatal: boolean } | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    api.listDrafts(session.gameId).then(setDrafts).catch(e => setError(e.message));
  };
  useEffect(reload, [session.gameId]);

  const validate = async () => {
    try {
      const report = await api.validateDrafts(session.gameId);
      setLint(report);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.publish(session.gameId, { user_id: session.userId, notes });
      setNotes(''); setLint(null); reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!confirm(`Discard ${drafts.length} draft(s)?`)) return;
    try {
      await api.discardDrafts(session.gameId);
      reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Container>
      <PageHeader
        title="Drafts & Publish"
        subtitle="Stage changes, validate, then publish atomically."
        actions={
          <>
            <button className="btn" onClick={validate}>
              Validate
            </button>
            <button
              className="btn btn-danger"
              onClick={discard}
              disabled={drafts.length === 0}
            >
              Discard all
            </button>
          </>
        }
      />

      {error && <div className="error-banner">{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-5">
        <div className="card">
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-2">
            Pending drafts
          </div>
          <div className="text-[26px] font-semibold tracking-tight tabular-nums">
            {drafts.length}
          </div>
        </div>
        <div className="card">
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-2">
            Lint
          </div>
          {lint ? (
            <div className="text-sm">
              {lint.has_fatal ? (
                <span className="text-bad">⚠ {lint.problems.length} fatal</span>
              ) : lint.problems.length > 0 ? (
                <span className="text-warn">{lint.problems.length} warnings</span>
              ) : (
                <span className="text-good">✓ Clean — ready to publish</span>
              )}
            </div>
          ) : (
            <div className="text-sm text-fg-muted">Run validate to check.</div>
          )}
        </div>
        <div className="card">
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-2">
            Status
          </div>
          <div className="text-sm">
            {drafts.length === 0 ? (
              <span className="text-fg-muted">Nothing to publish</span>
            ) : (
              <span className="text-fg-2">
                Ready to publish {drafts.length} change{drafts.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="card mb-5">
        <h3 className="m-0 mb-3 text-sm font-semibold">Publish snapshot</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="input"
            placeholder="Release notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
          <button
            className="btn btn-primary shrink-0"
            onClick={publish}
            disabled={busy || drafts.length === 0}
          >
            {busy ? 'Publishing…' : `Publish ${drafts.length} draft${drafts.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">
        Pending drafts
      </h2>
      {drafts.length === 0 ? (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">Nothing pending</div>
          <div className="text-xs text-fg-muted">
            Edit entities in Config Explorer to stage changes.
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Package</th>
                <th>Type</th>
                <th>Key</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map(d => (
                <tr key={d.id}>
                  <td className="text-fg-2">{d.package}</td>
                  <td className="text-fg-dim">{d.entity_type}</td>
                  <td>
                    <Link to={`/entities/${d.id}`} className="font-medium">
                      {d.entity_key}
                    </Link>
                  </td>
                  <td className="font-mono text-fg-dim">v{d.version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Container>
  );
}
